import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, TrendingUp, User, Activity, X, Camera } from 'lucide-react';
import Layout from '../components/Layout';
import { Btn, Spinner, Modal, Badge } from '../components/UI';
import { useAuth } from '../contexts/AuthContext';
import { usePatient } from '../contexts/PatientContext';
import { API_URL, API_KEY } from '../config';

// ─── Profile Avatar ───────────────────────────────────────────────────────────
function ProfileAvatar({ username }) {
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [hover, setHover] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!username) return;
    const stored = localStorage.getItem(`profile_photo_${username}`);
    if (stored) setPhoto(stored);
  }, [username]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB.'); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 256; canvas.height = 256;
        const ctx = canvas.getContext('2d');
        const size = Math.min(img.width, img.height);
        ctx.drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 0, 0, 256, 256);
        const resized = canvas.toDataURL('image/jpeg', 0.82);
        localStorage.setItem(`profile_photo_${username}`, resized);
        setPhoto(resized);
        setUploading(false);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    localStorage.removeItem(`profile_photo_${username}`);
    setPhoto(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <div
        style={{ position: 'relative', cursor: 'pointer' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => fileRef.current?.click()}
        title="Click to change photo"
      >
        <div style={{
          width: 88, height: 88, borderRadius: '50%',
          background: photo ? 'transparent' : 'linear-gradient(135deg, var(--blue) 0%, var(--teal) 100%)',
          boxShadow: hover ? '0 0 0 4px rgba(30,111,255,0.18)' : '0 0 0 2px rgba(30,111,255,0.10)',
          transition: 'box-shadow 0.2s',
          display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
        }}>
          {photo
            ? <img src={photo} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <User size={34} color="#fff" />
          }
        </div>

        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          opacity: hover ? 1 : 0, transition: 'opacity 0.2s', pointerEvents: 'none',
        }}>
          <Camera size={20} color="#fff" />
          <span style={{ fontSize: 9, color: '#fff', marginTop: 3, fontWeight: 600 }}>
            {uploading ? 'Saving…' : 'Change'}
          </span>
        </div>

        {photo && (
          <button onClick={handleRemove} title="Remove photo" style={{
            position: 'absolute', top: -2, right: -2,
            width: 22, height: 22, borderRadius: '50%',
            background: 'var(--red)', border: '2px solid #fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', opacity: hover ? 1 : 0, transition: 'opacity 0.2s', zIndex: 2,
          }}>
            <X size={11} color="#fff" />
          </button>
        )}
      </div>

      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>{username}</p>
        <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 1 }}>
          {photo ? 'Tap to update' : 'Tap to add photo'}
        </p>
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProfilePage() {
  const navigate = useNavigate();
  const { username } = useAuth();
  const { setPatient } = usePatient();
  const [results, setResults] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [monitorOpen, setMonitorOpen] = useState(false);
  const [monitorReport, setMonitorReport] = useState(null);
  const [monitorLoading, setMonitorLoading] = useState(false);
  const isSearching = useRef(false);
  const lastTerm = useRef('');

  useEffect(() => {
    if (username) fetchHistory();
  }, [username]);

  const fetchHistory = async () => {
    setLoading(true);
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) {
      setResults([]);
      setFiltered([]);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/results?email=${encodeURIComponent(userEmail)}`,
        { headers: { 'Cytometrix-API-KEY': API_KEY } }
      );
      const data = await res.json();
      if (!data || data.length === 0) {
        setResults([]);
        setFiltered([]);
        return;
      }

      let all = [];
      data.forEach(item => {
        if (item.visits && Array.isArray(item.visits)) {
          item.visits.forEach(v => all.push({
            ...v,
            patient_id: item.patient_id || v.patient_id,
            full_name: v.full_name || item.full_name,
          }));
        } else {
          all.push(item);
        }
      });

      all.sort((a, b) => {
        const ic = (a.patient_id || '').localeCompare(b.patient_id || '');
        return ic !== 0 ? ic : new Date(b.timestamp) - new Date(a.timestamp);
      });

      const totals = {}, counts = {};
      all.forEach(r => { totals[r.patient_id] = (totals[r.patient_id] || 0) + 1; });
      all = all.map(r => {
        const pid = r.patient_id;
        if (!counts[pid]) counts[pid] = totals[pid];
        return { ...r, visit_number: counts[pid]--, total_visits: totals[pid] };
      });

      setResults(all);
      if (!isSearching.current) setFiltered(all);
    } catch (e) {
      console.error('Failed to fetch history:', e);
      setResults([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  };

  // Search filter
  useEffect(() => {
    const term = search.toLowerCase().trim();
    if (!term) {
      isSearching.current = false;
      lastTerm.current = '';
      setFiltered(results);
      return;
    }
    if (term !== lastTerm.current) {
      isSearching.current = true;
      lastTerm.current = term;
      setFiltered(results.filter(r =>
        (r.patient_id || '').toString().toLowerCase().includes(term) ||
        (r.full_name || '').toLowerCase().includes(term) ||
        new Date(r.timestamp).toLocaleDateString().toLowerCase().includes(term)
      ));
    }
  }, [search, results]);

  useEffect(() => {
    if (!isSearching.current) setFiltered(results);
  }, [results]);

  const canMonitor = () => {
    if (!search.trim() || filtered.length < 2) return false;
    const groups = {};
    filtered.forEach(r => {
      if (!r.patient_id || !r.full_name) return;
      const key = `${r.patient_id}|||${r.full_name}`.toLowerCase();
      groups[key] = (groups[key] || 0) + 1;
    });
    const vals = Object.values(groups);
    return vals.length === 1 && vals[0] >= 2;
  };

  const handleMonitor = async () => {
    setMonitorLoading(true);
    try {
      const groups = {};
      filtered.forEach(r => {
        const key = `${r.patient_id}|||${r.full_name}`.toLowerCase();
        if (!groups[key]) groups[key] = { patientId: r.patient_id, patientName: r.full_name, records: [] };
        groups[key].records.push(r);
      });
      const target = Object.values(groups).find(g => g.records.length >= 2);
      if (!target) return;

      const sorted = [...target.records].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      const payload = {
        patient_id: target.patientId,
        patient_name: target.patientName,
        records: sorted.map(r => ({
          timestamp: r.timestamp,
          analysis: r.Analysis,
          analysisId: r.AnalysisID,
          report: r.report,
          id: r.id,
        })),
      };

      const res = await fetch(`${API_URL}/api/monitoring`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setMonitorReport(data);
        setMonitorOpen(true);
      }
    } catch (e) {
      console.error('Monitor error:', e);
    } finally {
      setMonitorLoading(false);
    }
  };

  const formatDate = (iso) => new Date(iso).toLocaleDateString();
  const formatTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const uniquePatients = new Set(results.map(r => r.patient_id)).size;
  const thisMonth = results.filter(r => new Date(r.timestamp).getMonth() === new Date().getMonth()).length;

  return (
    <Layout>
      <style>{`
        @media (max-width: 600px) {
          .profile-header { flex-direction: column !important; text-align: center; align-items: center !important; }
          .stats-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .search-row { flex-direction: column !important; }
          .search-row > * { width: 100%; }
        }
      `}</style>

      {/* Profile header */}
      <div className="profile-header" style={{
        background: '#fff', borderRadius: 16, padding: '20px 24px',
        border: '1px solid var(--gray-200)', boxShadow: 'var(--shadow-sm)',
        marginBottom: 20, display: 'flex', alignItems: 'center', gap: 24,
      }}>
        <ProfileAvatar username={username} />
        <div style={{ flex: 1 }}>
          <h2 style={{
            fontSize: 20, fontWeight: 700, color: 'var(--navy)',
            fontFamily: "'DM Serif Display', serif", margin: 0,
          }}>
            Diagnostic History
          </h2>
          <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 4 }}>
            {results.length} records across {uniquePatients} patients
          </p>
          <div style={{ marginTop: 14 }}>
            <Btn icon={Activity} onClick={() => navigate('/analysis')}>New Analysis</Btn>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 12, marginBottom: 24,
      }}>
        {[
          { label: 'Total Records', value: results.length, color: 'var(--blue)' },
          { label: 'Patients', value: uniquePatients, color: 'var(--teal)' },
          { label: 'This Month', value: thisMonth, color: 'var(--amber)' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: '#fff', borderRadius: 14, padding: '14px 16px',
            border: '1px solid var(--gray-200)', boxShadow: 'var(--shadow-sm)',
          }}>
            <p style={{ fontSize: 26, fontWeight: 700, color: 'var(--navy)', fontFamily: "'DM Serif Display', serif", margin: 0 }}>
              {value}
            </p>
            <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>{label}</p>
            <div style={{ height: 3, background: color, borderRadius: 2, width: '40%', marginTop: 8, opacity: 0.6 }} />
          </div>
        ))}
      </div>

      {/* Search & Monitor */}
      <div className="search-row" style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={16} style={{
            position: 'absolute', left: 14, top: '50%',
            transform: 'translateY(-50%)', color: 'var(--gray-400)', pointerEvents: 'none',
          }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by ID, name, or date…"
            style={{
              width: '100%', padding: '11px 16px 11px 42px',
              border: '1.5px solid var(--gray-200)', borderRadius: 10,
              fontSize: 14, fontFamily: "'DM Sans', sans-serif",
              outline: 'none', background: '#fff', boxSizing: 'border-box',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--blue)'}
            onBlur={e => e.target.style.borderColor = 'var(--gray-200)'}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)',
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>
        {canMonitor() && (
          <Btn variant="teal" icon={TrendingUp} loading={monitorLoading} onClick={handleMonitor}>
            Monitor
          </Btn>
        )}
      </div>

      {/* Records list */}
      {loading ? <Spinner /> : filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '48px 0',
          background: '#fff', borderRadius: 16, border: '1px solid var(--gray-200)',
        }}>
          <Activity size={36} color="var(--gray-300)" style={{ margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--gray-500)', fontSize: 14 }}>
            {search ? 'No records match your search.' : 'No medical records yet.'}
          </p>
          {!search && (
            <Btn style={{ marginTop: 14 }} onClick={() => navigate('/patient')}>
              Register First Patient
            </Btn>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((item, idx) => {
            const uniqueKey = `${item.patient_id}-${item.id || ''}-${item.timestamp}-${idx}`;
            return (
              <div
                key={uniqueKey}
                style={{
                  background: '#fff', borderRadius: 14, padding: '16px 18px',
                  border: '1px solid var(--gray-200)', boxShadow: 'var(--shadow-sm)',
                  transition: 'box-shadow 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow-sm)'}
              >
                {/* Top row */}
                <div style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', gap: 12, marginBottom: 10,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: 'var(--blue-pale)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <User size={18} color="var(--blue)" />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{
                        fontSize: 14, fontWeight: 700, color: 'var(--navy)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0,
                      }}>
                        {item.full_name || 'N/A'}
                      </p>
                      <p style={{
                        fontSize: 12, color: 'var(--gray-500)', margin: 0,
                        fontFamily: "'JetBrains Mono', monospace",
                      }}>
                        {item.patient_id || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/history', {
                      state: {
                        id: item.id || item.patient_id,
                        patient_id: item.patient_id,
                        timestamp: item.timestamp,
                      },
                    })}
                    style={{
                      width: 36, height: 36, borderRadius: 9,
                      background: 'var(--blue)', border: 'none',
                      cursor: 'pointer', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    <Eye size={16} color="#fff" />
                  </button>
                </div>

                {/* Bottom row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: 0 }}>
                    {formatDate(item.timestamp)} · {formatTime(item.timestamp)}
                  </p>
                  {item.visit_number && (
                    <Badge color="blue">Visit {item.visit_number}/{item.total_visits}</Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Monitoring Modal */}
      <Modal open={monitorOpen} onClose={() => setMonitorOpen(false)} title="Monitoring Report" maxWidth={820}>
        {monitorReport && <MonitoringReportContent report={monitorReport} />}
      </Modal>
    </Layout>
  );
}

// ─── Monitoring Report Content ────────────────────────────────────────────────
// Field mapping matched exactly to /api/monitoring Flask response:
//
//   report.trend                  — "Improving" | "Stable" | "Declining" | "Needs Attention"
//   report.patient_name / patient_id / total_records / monitoring_period / parameters_tracked
//   report.ai_advice
//   report.comparison_summary
//   report.critical_changes[]     — |percent_change| >= 25
//   report.significant_changes[]  — |percent_change| 15–25
//   report.moderate_changes[]     — |percent_change| 5–15
//   report.stable_parameters[]    — |percent_change| < 5
//   report.improving_parameters[]
//   report.worsening_parameters[]
//   report.statistics{}
//
//   Each item: { analysis_type, earlier_raw, later_raw, change, percent_change, trend, unit, ref_range, parameter }

function MonitoringReportContent({ report }) {
  const trend = (report.trend || 'Stable').toLowerCase().replace(/\s+/g, '_');

  const trendConfig = {
    improving:       { label: 'Improving',       bg: '#EAF3DE', color: '#27500A', border: '#97C459', dot: '#639922' },
    stable:          { label: 'Stable',          bg: '#F1EFE8', color: '#444441', border: '#B4B2A9', dot: '#888780' },
    declining:       { label: 'Declining',       bg: '#FCEBEB', color: '#791F1F', border: '#F09595', dot: '#E24B4A' },
    needs_attention: { label: 'Needs Attention', bg: '#FAEEDA', color: '#633806', border: '#EF9F27', dot: '#BA7517' },
  };
  const tc = trendConfig[trend] || trendConfig.stable;

  // ── Status config: Improved / Deteriorated / Stable ──
  const statusConfig = {
    improved:    { bg: '#EAF3DE', color: '#27500A', dot: '#639922', label: 'Improved' },
    'no change': { bg: '#F1EFE8', color: '#444441', dot: '#888780', label: 'Stable'   },
    deteriorated:    { bg: '#FCEBEB', color: '#791F1F', dot: '#E24B4A', label: 'Deteriorated' },
  };

  // later_raw values that mean the parameter has fully normalised → always Improved
  const NORMAL_VALUES = new Set(['normal', 'negative', 'none', 'undetected', 'absent', 'not detected']);

  const worseningKeys = new Set(
    (report.worsening_parameters || []).map(p => p.parameter || p.analysis_type)
  );
  const improvingKeys = new Set(
    (report.improving_parameters || []).map(p => p.parameter || p.analysis_type)
  );

  const getStatus = (item, severity) => {
    const key    = item.parameter || item.analysis_type;
    const change = item.change || 0;
    const pct    = item.percent_change || 0;

    const isZeroBaseline  = item.earlier_value === 0 || item.earlier_value === '0';
    const hasActualChange = isZeroBaseline
      ? Math.abs(change) > 0
      : Math.abs(pct) >= 5;

    if (!hasActualChange) return 'no change';

    // If later_raw is a normalised label (e.g. "Normal", "Negative"),
    // the parameter has resolved regardless of numeric direction → Improved.
    const laterNormalised = NORMAL_VALUES.has((item.later_raw || '').toLowerCase().trim());
    if (laterNormalised) return 'improved';

    // API-supplied sets take priority over heuristics
    if (worseningKeys.has(key)) return 'deteriorated';
    if (improvingKeys.has(key)) return 'improved';

    // Severity-bucket fallbacks
    if (severity === 'critical' || severity === 'significant') return 'deteriorated';
    if (severity === 'stable') return 'no change';

    // Moderate: direction decides
    return change > 0 ? 'improved' : 'deteriorated';
  };

  // Re-bucket zero-baseline items that Flask incorrectly lands in stable_parameters
  // because percent_change = (change / 0 * 100) = 0 even when change is large.
  const reclassifySeverity = (item) => {
    const isZeroBaseline = item.earlier_value === 0 || item.earlier_value === '0';
    if (!isZeroBaseline) return item.severity;
    const absChange = Math.abs(item.change || 0);
    if (absChange === 0)  return 'stable';
    if (absChange >= 50)  return 'critical';
    if (absChange >= 20)  return 'significant';
    return 'moderate';
  };

  const allRows = [
    ...(report.critical_changes    || []).map(r => ({ ...r, severity: 'critical'    })),
    ...(report.significant_changes || []).map(r => ({ ...r, severity: 'significant' })),
    ...(report.moderate_changes    || []).map(r => ({ ...r, severity: 'moderate'    })),
    ...(report.stable_parameters   || []).map(r => ({ ...r, severity: 'stable'      })),
  ]
    .map(r => ({ ...r, severity: reclassifySeverity(r) }))
    .sort((a, b) => {
      const order = { critical: 0, significant: 1, moderate: 2, stable: 3 };
      return (order[a.severity] ?? 3) - (order[b.severity] ?? 3);
    });

  const thSt = {
    padding: '9px 12px',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--gray-500)',
    borderBottom: '1px solid var(--gray-200)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    textAlign: 'left',
    background: '#fafafa',
    whiteSpace: 'nowrap',
  };

  const tdSt = (isLast) => ({
    padding: '10px 12px',
    fontSize: 13,
    color: 'var(--navy)',
    borderBottom: isLast ? 'none' : '1px solid var(--gray-100)',
    verticalAlign: 'middle',
  });

  const sectionBox = {
    background: '#fafafa',
    borderRadius: 12,
    padding: '14px 16px',
    border: '1px solid var(--gray-200)',
  };

  const sectionLabel = {
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--gray-400)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 7px',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* ── Meta strip ── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
        background: '#fafafa', borderRadius: 12, padding: '14px 16px',
        border: '1px solid var(--gray-200)',
      }}>
        {[
          ['Patient',            report.patient_name       || '—'],
          ['Patient ID',         report.patient_id         || '—'],
          ['Visits',             report.total_records      || '—'],
          ['Period',             report.monitoring_period  || '—'],
          ['Parameters tracked', report.parameters_tracked || '—'],
        ].map(([label, value]) => (
          <div key={label}>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>
              {label}
            </p>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginTop: 3, marginBottom: 0 }}>
              {value}
            </p>
          </div>
        ))}
      </div>

      {/* ── Trend pill + stat chips ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>
          Overall trend
        </p>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 13px', borderRadius: 20, fontSize: 12, fontWeight: 700,
          background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`,
        }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: tc.dot, display: 'inline-block', flexShrink: 0 }} />
          {tc.label}
        </span>

        {/* Stat chips — counted from the rendered rows, not the raw API statistics */}
        {(() => {
          const improved = allRows.filter(r => getStatus(r, r.severity) === 'improved').length;
          const deteriorated = allRows.filter(r => getStatus(r, r.severity) === 'deteriorated').length;
          const stable   = allRows.filter(r => getStatus(r, r.severity) === 'no change').length;
          return [
            { label: 'Improved', count: improved, bg: '#EAF3DE', color: '#27500A' },
            { label: 'Deteriorated', count: deteriorated, bg: '#FCEBEB', color: '#791F1F' },
            { label: 'Stable',   count: stable,   bg: '#F1EFE8', color: '#5F5E5A' },
          ].filter(s => s.count > 0).map(s => (
            <span key={s.label} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
              background: s.bg, color: s.color,
            }}>
              {s.count} {s.label}
            </span>
          ));
        })()}
      </div>

      {/* ── Parameters table (no Severity column) ── */}
      {allRows.length > 0 ? (
        <div style={{ border: '1px solid var(--gray-200)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
              <thead>
                <tr>
                  {['Parameter', 'From', 'To', 'Change', 'Status'].map(h => (
                    <th key={h} style={thSt}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allRows.map((item, i) => {
                  const isLast     = i === allRows.length - 1;
                  const statusKey  = getStatus(item, item.severity);
                  const sc         = statusConfig[statusKey] || statusConfig['no change'];
                  const pct        = item.percent_change || 0;
                  const isZeroBase = item.earlier_value === 0 || item.earlier_value === '0';

                  // Arrow colour follows status outcome, not raw numeric direction
                  const arrowColor =
                    statusKey === 'improved' ? '#639922' :
                    statusKey === 'deteriorated' ? '#E24B4A' : '#888780';

                  const changeDisplay = (() => {
                    if (isZeroBase && Math.abs(item.change || 0) > 0) {
                      const arrow = (item.change || 0) > 0 ? '↑' : '↓';
                      return (
                        <>
                          {arrow} {Math.abs(item.change).toFixed(1)}
                          <span style={{ fontSize: 10, fontWeight: 400 }}> (new)</span>
                        </>
                      );
                    }
                    const arrow = pct > 0 ? '↑' : pct < 0 ? '↓' : '→';
                    return <>{arrow} {Math.abs(pct).toFixed(1)}%</>;
                  })();

                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafcff' }}>

                      {/* Parameter name + unit + ref range */}
                      <td style={{ ...tdSt(isLast), fontWeight: 700, fontSize: 12 }}>
                        {item.analysis_type || item.parameter || '—'}
                        {item.unit ? (
                          <span style={{ fontWeight: 400, color: 'var(--gray-400)', marginLeft: 3, fontSize: 11 }}>
                            ({item.unit})
                          </span>
                        ) : null}
                        {item.ref_range ? (
                          <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 2, fontWeight: 400 }}>
                            Ref: {item.ref_range}
                          </div>
                        ) : null}
                      </td>

                      {/* From */}
                      <td style={{ ...tdSt(isLast), fontFamily: 'monospace', fontSize: 12, color: 'var(--gray-500)' }}>
                        {item.earlier_raw || '—'}
                      </td>

                      {/* To */}
                      <td style={{ ...tdSt(isLast), fontFamily: 'monospace', fontSize: 12 }}>
                        {item.later_raw || '—'}
                      </td>

                      {/* Change */}
                      <td style={tdSt(isLast)}>
                        <span style={{ color: arrowColor, fontWeight: 700, fontSize: 13 }}>
                          {changeDisplay}
                        </span>
                      </td>

                      {/* Status pill — Improved / Deteriorated / Stable */}
                      <td style={tdSt(isLast)}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '3px 9px', borderRadius: 20,
                          fontSize: 11, fontWeight: 700,
                          background: sc.bg, color: sc.color,
                          whiteSpace: 'nowrap',
                        }}>
                          <span style={{
                            width: 6, height: 6, borderRadius: '50%',
                            background: sc.dot, display: 'inline-block', flexShrink: 0,
                          }} />
                          {sc.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={{
          textAlign: 'center', padding: '32px 0',
          background: '#fafafa', borderRadius: 12,
          border: '1px solid var(--gray-200)',
          color: 'var(--gray-400)', fontSize: 13,
        }}>
          No parameter changes detected between visits.
        </div>
      )}

      {/* ── Legend ── */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'Improved', dot: '#639922' },
          { label: 'Deteriorated', dot: '#E24B4A' },
          { label: 'Stable',   dot: '#888780' },
        ].map(({ label, dot }) => (
          <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--gray-500)' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, display: 'inline-block' }} />
            {label}
          </span>
        ))}
        <span style={{ fontSize: 11, color: 'var(--gray-400)', marginLeft: 'auto' }}>
          &lt;5% stable · 5–15% moderate · 15–25% significant · &gt;25% critical
        </span>
      </div>

      {/* ── AI Recommendations ── */}
      {report.ai_advice && (
        <div style={sectionBox}>
          <p style={sectionLabel}>AI Recommendations</p>
          <p style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.75, margin: 0, whiteSpace: 'pre-wrap' }}>
            {report.ai_advice}
          </p>
        </div>
      )}

      {/* ── Comparison summary ── */}
      {report.comparison_summary && (
        <div style={sectionBox}>
          <p style={sectionLabel}>Summary</p>
          <p style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.75, margin: 0 }}>
            {report.comparison_summary}
          </p>
        </div>
      )}

    </div>
  );
}
